Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "AtlasBuilder", {
    enumerable: true,
    get: function() {
        return AtlasBuilder;
    }
});
const _atlas = require("./atlas.cjs");
const _env = require("./env.cjs");
const _errors = require("./errors.cjs");
const _fetcher = require("./fetcher.cjs");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
class AtlasBuilder {
    static makeUrl(version) {
        return new URL(`https://img6.wsimg.com/ux-assets/atlas/${version}/definitions.json`);
    }
    setUrl(url) {
        if (this.defSource) {
            this.logger.warn(`Definitions source already set using ${this.defSource} method.`);
            return this;
        }
        this.defSource = 'setUrl';
        this.url = url instanceof URL ? url : new URL(url);
        return this;
    }
    setVersion(version) {
        if (this.defSource) {
            this.logger.warn(`Definitions source already set using ${this.defSource} method.`);
            return this;
        }
        this.defSource = 'setVersion';
        this.url = AtlasBuilder.makeUrl(version);
        return this;
    }
    setUpdateInterval(interval) {
        if (this.atlasDef) {
            this.logger.warn(`Definitions have already been set using .setJson method. Updates will be ignored.`);
        } else if (typeof interval !== 'number' || interval < 0) {
            this.logger.warn('Update interval must be a non-negative number. Setting to 0.');
            this.interval = 0;
        } else {
            this.interval = interval;
        }
        return this;
    }
    setNoUpdate() {
        this.interval = 0;
        return this;
    }
    setLogger(logger) {
        this.logger = logger;
        return this;
    }
    /**
     * @deprecated Use default caching behavior instead. To disable caching, use setNoCache().
     */ setSingleton() {
        return this;
    }
    setNoCache() {
        this.cachable = false;
        return this;
    }
    setJson(json) {
        if (this.defSource) {
            this.logger.warn(`Definitions source already set using ${this.defSource} method.`);
            return this;
        }
        if (!json || typeof json !== 'object' || !json.version) {
            throw new _errors.AtlasParseError('Invalid AtlasDef JSON provided');
        }
        this.defSource = 'setJson';
        this.interval = 0;
        this.atlasDef = json;
        return this;
    }
    build() {
        let cacheKey;
        // Check for singleton cache if caching is enabled (default behavior unless setNoCache() was called)
        if (this.cachable) {
            cacheKey = `${this.url.toString()}-${this.env}-${this.interval}`;
            const cachedAtlas = AtlasBuilder.cache.get(cacheKey);
            if (cachedAtlas) {
                return Promise.resolve(cachedAtlas);
            }
            // Check for in-flight build promise to handle concurrent builds
            const existingPromise = AtlasBuilder.promiseCache.get(cacheKey);
            if (existingPromise) {
                return existingPromise;
            }
        }
        // Create the build promise
        let promise = this.performBuild();
        if (!cacheKey) {
            // If caching is disabled, we don't need to cache the promise
            return promise;
        }
        // Add cache handling for the promise
        promise = promise.then((atlas)=>{
            // Cache the final result
            AtlasBuilder.cache.set(cacheKey, atlas);
            return atlas;
        }).finally(()=>{
            // Clean up the promise cache
            AtlasBuilder.promiseCache.delete(cacheKey);
        });
        AtlasBuilder.promiseCache.set(cacheKey, promise);
        return promise;
    }
    async performBuild() {
        try {
            const fetcher = new _fetcher.AtlasFetcher(this.url, this.interval, this.logger);
            if (!this.atlasDef) {
                this.atlasDef = await fetcher.fetch();
            }
            if (!this.atlasDef) {
                throw new _errors.AtlasFetchError(`Failed to fetch definitions from ${this.url}`);
            }
            let atlas;
            try {
                atlas = new _atlas.Atlas(this.env, this.atlasDef, fetcher);
            } catch (err) {
                throw new _errors.AtlasParseError(`Failed to instantiate Atlas: ${err instanceof Error ? err.message : String(err)}`);
            }
            return atlas;
        } catch (err) {
            // Propagate error
            if (err instanceof _errors.AtlasError) throw err;
            throw new _errors.AtlasError(`AtlasBuilder build failed: ${err instanceof Error ? err.message : String(err)}`);
        }
    }
    constructor(env){
        _define_property(this, "env", void 0);
        _define_property(this, "url", void 0);
        _define_property(this, "interval", 10 * 60 * 1000); // 10 minutes
        _define_property(this, "atlasDef", void 0);
        // track which method was used for setting def source
        _define_property(this, "defSource", '');
        _define_property(this, "logger", console);
        _define_property(this, "cachable", true);
        // Validate env
        try {
            this.env = (0, _env.envFromString)(env);
        } catch (e) {
            throw new _errors.AtlasError(`Invalid environment: ${env}`);
        }
        const versionEnv = this.env === 'ote' || this.env === 'stg' ? 'prod' : this.env;
        this.url = AtlasBuilder.makeUrl(versionEnv);
    }
}
// Static singleton cache
_define_property(AtlasBuilder, "cache", new Map());
// Cache for in-flight build promises to handle concurrent builds
_define_property(AtlasBuilder, "promiseCache", new Map());
